import { toast } from "./toast";

/**
 * Footage Search → user-picked local folder bundle download. Uses File
 * System Access API (Chrome/Edge) to write files in the exact folder
 * structure the editor wants:
 *
 *   <root>/Footage - MM.DD.YY/
 *   ├── A-roll/
 *   │   ├── <clip>.mp4
 *   │   └── sidecars/
 *   │       ├── <clip>.txt              ← from transcript_text (DB)
 *   │       └── <clip>.description.txt  ← from ai_description (DB)
 *   ├── B-roll/
 *   │   ├── <PatientName>/
 *   │   │   ├── <clip>.mp4
 *   │   │   ├── <consent>.pdf           ← every PDF in this patient's
 *   │   │   │                              Drive folder, lives next to
 *   │   │   │                              their clips
 *   │   │   └── sidecars/
 *   │   │       ├── <clip>.txt
 *   │   │       └── <clip>.description.txt
 *   │   └── <AnotherPatient>/ (same layout)
 *   └── manifest.txt                    ← clip→source mapping
 *
 * Notes:
 *  - Patient folder names come from Drive (the DB intentionally doesn't
 *    store them). Sanitized to a filesystem-safe form before use.
 *  - Local-only clips (no driveFileId) skip the video; their sidecars
 *    still land. B-roll local-only clips can't determine a patient name
 *    either, so they land in B-roll/_Unknown patient/. The manifest
 *    flags them.
 *  - All sidecars (.description.txt / .txt / .srt) come fresh from the
 *    DB at download time — they don't get written to Drive at upload
 *    time. Footage Search bundle is the only path that materializes
 *    them as files.
 *  - The whole flow is best-effort per-file: one Drive 403 or network
 *    blip doesn't void the bundle. The end toast reports counts.
 */

export interface BundleClip {
  id: number;
  subjectKind: string | null;
  /**
   * Denormalized patient/provider name stored on the row. Used as the
   * B-roll subfolder name for NAS / local-only patient rows, which
   * carry a real subjectName but an EMPTY driveFolderId (no Drive
   * folder identity). Without this those clips land under
   * "_Unknown patient" even though the search card shows the name.
   * Optional so older server payloads / other metadata builders still
   * satisfy the type.
   */
  subjectName?: string | null;
  /**
   * "video" (default, includes image stills) or "audio". Audio rows
   * route into a sibling `Audio - MM.DD.YY/` folder instead of the
   * main `Footage - MM.DD.YY/` bundle. Tolerates older server payloads
   * by treating undefined / unknown as "video".
   */
  mediaKind?: string;
  renamedName: string;
  driveFileId: string | null;
  /** NAS-relative path for rows sourced from the NAS. When a row has no
   *  driveFileId but has a nasPath, the video still streams — the server's
   *  /api/footage/stream falls back to the NAS Serve API over Tailscale.
   *  EXCEPTION: reference_only rows also carry a nasPath but are NOT
   *  streamable (it points at a giant master we never moved) — see below. */
  nasPath: string | null;
  /** Oversized "reference-only" clip. The full master lives only on the NAS
   *  and is never bundled; instead we write a small NAS-path pointer .txt so
   *  the editor knows where to grab it. Tolerates older server payloads by
   *  treating undefined as false. */
  referenceOnly?: boolean;
  driveFolderId: string;
  aiDescription: string | null;
  transcriptText: string | null;
  transcriptSrt: string | null;
}

export interface BundleConsent {
  patientFolderId: string;
  /** Drive folder display name — used to build B-roll/<patientName>/. */
  patientName: string | null;
  pdfs: Array<{ id: string; name: string }>;
}

export interface BundleMetadata {
  clips: BundleClip[];
  consents: BundleConsent[];
}

/**
 * A single file that failed to download, with the identifiers the success
 * screen needs to act on it. Richer than the `failedNames`/`errors` display
 * strings: `clipId` is the footage file id for video/audio rows (maps to
 * `/api/footage/stream?fileId=`, so a retry can re-request exactly the failed
 * clips) and `driveFileId` is the Drive file id when the row has one (Drive
 * clips + consent PDFs), so the screen can offer an "Open in Drive" link.
 * NAS-only rows carry `driveFileId: null` (no Drive viewer to link to).
 */
export interface BundleFailure {
  name: string;
  kind: "video" | "audio" | "consent";
  clipId: number | null;
  driveFileId: string | null;
  error: string;
}

export interface BundleResult {
  videoSucceeded: number;
  videoSkipped: number;
  videoFailed: number;
  /** Videos the user skipped mid-download via the per-file Skip
   *  button. These also count toward `videoSucceeded` because the
   *  deliverable (a Drive shortcut) did land — this field is just the
   *  subset that ended up as shortcuts rather than full bytes. */
  videoConverted: number;
  sidecarsWritten: number;
  consentSucceeded: number;
  consentFailed: number;
  errors: string[];
  /** Just the file names of the counted failures (video + audio +
   *  consent), in the order they failed. Surfaced directly in the
   *  summary toast so the user doesn't have to open the browser
   *  console to see WHICH files failed. `errors` still holds the full
   *  detail (name + HTTP status / message) for deeper debugging. */
  failedNames: string[];
  /** Structured per-failure records (see BundleFailure). Parallel to
   *  `failedNames`/`errors` but carries the clip / Drive identifiers the
   *  success screen uses to render "Open in Drive" links and to retry just
   *  the failed clips rather than the whole bundle. */
  failures: BundleFailure[];
  /** Bundle folder name created under the user-picked root — e.g.
   *  "Footage - 06.03.26". Carried back so the success screen can
   *  reference the exact folder the user just received. */
  bundleName: string;
  /** Sibling bundle folder name, e.g. "Audio - 06.03.26", created
   *  ALONGSIDE `bundleName` at the user-picked root when one or more
   *  audio rows were in the selection. `null` when no audio was
   *  included. Created lazily — sidecars-only bundles leave this
   *  null (the audio sidecars land inside the main bundle's per-
   *  patient sidecars folder, no separate Audio folder needed). */
  audioBundleName: string | null;
  /** How many audio files were successfully delivered (bytes or
   *  shortcut, depending on videoMode). Counts the parallel `Audio - MM.DD.YY/`
   *  folder only — sidecars-only bundles report 0 here. */
  audioSucceeded: number;
  /** Audio files where the byte stream or shortcut write failed. */
  audioFailed: number;
  /** Whether videos in this bundle were full downloads or .url
   *  shortcuts — carried back so the success screen can match its
   *  copy to what the user actually received. */
  videoMode: VideoMode;
}

/**
 * How videos are delivered to the user's local folder.
 *   - "download": stream the .mp4 / .mov bytes through the server
 *     proxy. The default; the user gets the whole video on disk.
 *   - "shortcut": write a tiny .url file (Windows InternetShortcut
 *     format, readable on macOS / Linux as plain text) pointing at
 *     the Drive viewer URL. Sidecars + consents still download
 *     normally — only the video bytes are skipped. Useful when a
 *     bundle has multi-GB clips the user doesn't need locally right
 *     now and would rather pull from Drive on demand.
 *   - "sidecars-only": skip both the video AND the .url shortcut.
 *     Pure sidecars + consents. Used by the patient-sidecars action
 *     where the editor already has the videos in Drive and just
 *     needs the descriptions/transcripts/SRTs.
 */
export type VideoMode = "download" | "shortcut" | "sidecars-only";

/**
 * Streamed-download progress snapshot. Fires repeatedly as bytes arrive.
 * Throttled to ~10 events/sec inside the library so React doesn't get
 * hammered on fast connections.
 *
 *   - totalFiles: streaming tasks queued for this bundle (videos with
 *     a Drive copy + consent PDFs). Sidecars, .url shortcuts, and
 *     local-only clips don't count — they're tiny inline writes.
 *   - completed: tasks that have finished (success or failure).
 *   - inFlight: tasks currently streaming. Length is capped at the
 *     pool's concurrency (4). Each entry tracks bytes for that one
 *     stream so the UI can show a per-file bar AND an overall bar.
 */
export interface BundleProgress {
  totalFiles: number;
  completed: number;
  inFlight: Array<{
    /** Stable React key — task index + filename. */
    key: string;
    name: string;
    received: number;
    total: number | null;
    /** "video" entries can be skipped mid-stream and converted into a
     *  .url Drive shortcut. "consent" PDFs are full downloads with no
     *  shortcut equivalent — the UI hides the skip button for these. */
    kind: "video" | "consent";
    /** Mid-stream skip. For a video task, aborts the in-flight fetch
     *  and writes a tiny .url shortcut in place of the video; counts
     *  as a success for the bundle. No-op when undefined (consents). */
    skip?: () => void;
  }>;
}

function formatMMDDYY(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}.${pad(d.getDate())}.${String(d.getFullYear()).slice(-2)}`;
}

function baseName(filename: string): string {
  return filename.replace(/\.[^.]+$/, "");
}

/**
 * Strip characters that aren't safe in a single path segment across
 * macOS, Windows, and the File System Access API spec. Replaces the
 * illegal set (/ \ : * ? " < > |) with underscores, collapses runs of
 * whitespace, and trims leading/trailing dots + spaces (Windows hates
 * those). Returns "" for input that sanitizes away entirely so callers
 * can apply their own fallback.
 *
 * Patient names carry double-quotes around a preferred name (e.g.
 * `Jane "JJ" Doe`) and we want the on-disk folder to still read with
 * quotes. But the File System Access API rejects the straight `"` on
 * EVERY platform (not just Windows) — getFileHandle/getDirectoryHandle
 * throw — so we can't keep it literally. Instead we translate straight
 * quotes to curly ones (`" "` / `' '`), which the API accepts and which
 * read the same to a human, BEFORE the illegal-char pass.
 */
function sanitizeNamePart(s: string): string {
  return s
    .replace(/"([^"]*)"/g, "“$1”") // paired "..." → curly “ ... ”
    .replace(/"/g, "”") // any stray straight " → curly ”
    .replace(/'([^']*)'/g, "‘$1’") // paired '...' → curly ‘ ... ’
    .replace(/[/\\:*?<>|]/g, "_")
    .replace(/\s+/g, " ")
    .replace(/^[.\s]+|[.\s]+$/g, "")
    .trim();
}

/**
 * Sanitize a FOLDER name. Falls back to a stable placeholder for empty
 * / null input or names that sanitize away to nothing.
 */
function safeFolderName(name: string | null | undefined, fallback: string): string {
  if (!name) return fallback;
  return sanitizeNamePart(name) || fallback;
}

/**
 * Sanitize a FILE name while preserving its extension. Splits on the
 * last dot, cleans the base with the same rules as folder names, and
 * re-attaches the (also-cleaned) extension. Guarantees a non-empty base
 * so we never try to create a file named just ".mov". Mirrors
 * safeFolderName so a file and its containing folder sanitize
 * identically — the manifest and the on-disk name stay in sync.
 */
function safeFileName(name: string): string {
  const dot = name.lastIndexOf(".");
  const hasExt = dot > 0 && dot < name.length - 1;
  const base = hasExt ? name.slice(0, dot) : name;
  const ext = hasExt ? name.slice(dot) : "";
  const cleanedBase = sanitizeNamePart(base) || "file";
  const cleanedExt = ext.replace(/[/\\:*?"<>|]/g, "");
  return cleanedBase + cleanedExt;
}

/**
 * Resolve the B-roll grouping key + display folder name for a patient
 * clip. Shared by the on-disk folder routing and the manifest so the
 * two never disagree.
 *
 *   - Drive-backed row (non-empty driveFolderId): group by folder ID,
 *     name from the Drive consent lookup, falling back to the row's own
 *     subjectName, then a stable "Patient <id>" placeholder.
 *   - NAS / local-only row (empty driveFolderId): there is no Drive
 *     folder to name or to hold consents, so group by (and display) the
 *     row's subjectName. Only a genuinely nameless row lands in
 *     "_Unknown patient".
 *
 * `nameByFolder` maps a Drive folder ID → its already-sanitized display
 * name (built from the consent metadata).
 */
function resolvePatientGroup(
  clip: BundleClip,
  nameByFolder: Map<string, string>,
): { key: string; name: string } {
  if (clip.driveFolderId) {
    const name =
      nameByFolder.get(clip.driveFolderId) ??
      safeFolderName(clip.subjectName, `Patient ${clip.driveFolderId.slice(0, 8)}`);
    return { key: clip.driveFolderId, name };
  }
  const name = safeFolderName(clip.subjectName, "_Unknown patient");
  return { key: `name:${name.toLowerCase()}`, name };
}

async function writeTextFile(
  dir: FileSystemDirectoryHandle,
  filename: string,
  content: string,
): Promise<void> {
  const fh = await dir.getFileHandle(filename, { create: true });
  const w = await fh.createWritable();
  await w.write(content);
  await w.close();
}

async function streamUrlToFile(
  dir: FileSystemDirectoryHandle,
  filename: string,
  url: string,
  onChunk?: (received: number, total: number | null) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(url, signal ? { signal } : {});
  if (!res.ok) {
    const body = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${body.slice(0, 200)}`);
  }
  const totalHeader = res.headers.get("content-length");
  const total = totalHeader ? parseInt(totalHeader, 10) : null;
  const fh = await dir.getFileHandle(filename, { create: true });
  const writable = await fh.createWritable();
  // Manual reader loop (instead of pipeTo) so we can observe bytes
  // as they stream through and report progress to the UI. Memory
  // footprint is the same — each chunk is written to disk and
  // released before the next read.
  if (res.body) {
    const reader = res.body.getReader();
    let received = 0;
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        received += value.byteLength;
        await writable.write(value);
        onChunk?.(received, total);
      }
    } finally {
      reader.releaseLock();
      // If the stream was aborted mid-write the partial file is
      // unusable — abort the writable so the FSA doesn't commit a
      // truncated file to disk. close() on a non-aborted writer
      // finalizes the file.
      if (signal?.aborted) {
        await writable.abort().catch(() => {});
      } else {
        await writable.close();
      }
    }
  } else {
    // Fallback: some older browsers don't expose ReadableStream on
    // Response.body — fall back to blob().
    const blob = await res.blob();
    await writable.write(blob);
    await writable.close();
    onChunk?.(blob.size, blob.size);
  }
}

function buildManifest(
  meta: BundleMetadata,
  downloadDate: Date,
  videoMode: VideoMode,
  shortcutClipIds: Set<number>,
  writeSidecars: boolean,
): string {
  const lines: string[] = [
    `Footage Bundle - ${downloadDate.toLocaleString()}`,
    ``,
  ];
  if (videoMode === "sidecars-only") {
    lines.push(
      `Sidecars-only bundle — no video files included. Sidecars`,
      `(.description.txt / .txt / .srt) and consent PDFs were`,
      `downloaded; videos live in Drive and can be pulled from`,
      `there using the original filenames listed below.`,
      ``,
    );
  } else if (videoMode === "shortcut") {
    lines.push(
      `Video files were delivered as .url shortcuts — open one to`,
      `download the video from Drive on demand. Sidecars and consent`,
      `forms were downloaded normally.`,
      ``,
    );
  } else if (shortcutClipIds.size > 0) {
    // Mixed-mode bundle: most videos downloaded as bytes, but the user
    // skipped one or more mid-stream and they landed as .url shortcuts.
    lines.push(
      `${shortcutClipIds.size} video${shortcutClipIds.size === 1 ? " was" : "s were"} ` +
        `skipped mid-download and replaced with .url shortcuts — open those`,
      `to fetch the video from Drive on demand.`,
      ``,
    );
  }

  // Oversized reference clips — flag them up front so the editor knows a
  // few entries below are pointers, not video files.
  const refCount = meta.clips.filter((c) => c.referenceOnly).length;
  if (refCount > 0) {
    lines.push(
      `${refCount} oversized reference clip${refCount === 1 ? "" : "s"} ` +
        `could not be included — ${refCount === 1 ? "it is" : "they are"} too large to`,
      `bundle. Each is represented by a "<name>.NAS-PATH.txt" pointer file`,
      `containing the exact NAS path where the full master lives. Their`,
      `transcripts / AI descriptions are still included as sidecars.`,
      ``,
    );
  }

  const aRoll = meta.clips.filter((c) => c.subjectKind === "provider");
  const bRoll = meta.clips.filter((c) => c.subjectKind === "patient");
  const other = meta.clips.filter(
    (c) => c.subjectKind !== "provider" && c.subjectKind !== "patient",
  );

  // Patient-folder-id → { name, consents } for the B-roll grouping.
  const patientByFolder = new Map<
    string,
    { name: string; consents: string[] }
  >();
  for (const c of meta.consents) {
    patientByFolder.set(c.patientFolderId, {
      name: safeFolderName(c.patientName, `Patient ${c.patientFolderId.slice(0, 8)}`),
      // Sanitized to match the names the consent PDFs actually land under.
      consents: c.pdfs.map((p) => safeFileName(p.name)),
    });
  }

  /**
   * Render a clip's filename as it actually landed on disk:
   *   - missing driveFileId    → just the name, with a [local-only] note
   *   - any clip in shortcutIds → "<base>.url" with a [Drive shortcut] note
   *   - everything else         → "<renamedName>" (the actual video file)
   *
   * `shortcutClipIds` covers both modes: bundle-wide shortcut mode adds
   * every clip up-front in Phase 1, and per-file mid-stream skips add
   * one at a time in Phase 2.
   */
  const formatClipFile = (c: BundleClip): string => {
    const base = sanitizeNamePart(baseName(c.renamedName)) || "clip";
    if (c.referenceOnly) {
      // Oversized master — only a NAS-path pointer is in the bundle. Checked
      // FIRST because reference rows have no driveFileId and would otherwise
      // be mislabeled "local-only" by the guard below.
      return `${base}.NAS-PATH.txt  [oversized — full file on NAS: ${c.nasPath ?? "path unavailable"}]`;
    }
    if (!c.driveFileId && !c.nasPath) {
      return `${safeFileName(c.renamedName)}  [video not available — local-only upload]`;
    }
    if (shortcutClipIds.has(c.id)) {
      return `${base}.url  [Drive shortcut — open to download video]`;
    }
    return safeFileName(c.renamedName);
  };

  // A-Roll: flat list under A-roll/.
  if (aRoll.length > 0) {
    lines.push(`A-Roll (${aRoll.length} clip${aRoll.length === 1 ? "" : "s"}):`);
    for (const c of aRoll) {
      lines.push(`  - A-roll/${formatClipFile(c)}`);
    }
    lines.push(``);
  }

  // B-Roll: grouped by patient subfolder. Each group shows its consents.
  if (bRoll.length > 0) {
    lines.push(`B-Roll (${bRoll.length} clip${bRoll.length === 1 ? "" : "s"}):`);
    // Group clips using the SAME resolver the on-disk routing uses, so
    // the manifest's patient headings match the actual folders. First-
    // seen order is preserved so the manifest reads top-down with the
    // most-recently-relevant patients first.
    const nameByFolder = new Map(
      [...patientByFolder].map(([id, p]) => [id, p.name] as const),
    );
    const byPatient = new Map<
      string,
      { name: string; driveFolderId: string; clips: BundleClip[] }
    >();
    for (const c of bRoll) {
      const { key, name } = resolvePatientGroup(c, nameByFolder);
      const group = byPatient.get(key) ?? { name, driveFolderId: c.driveFolderId, clips: [] };
      group.clips.push(c);
      byPatient.set(key, group);
    }
    for (const { name: displayName, driveFolderId, clips } of byPatient.values()) {
      lines.push(`  ${displayName}/`);
      for (const c of clips) {
        lines.push(`    - ${formatClipFile(c)}`);
      }
      // Consents only exist for Drive-backed patient folders. A NAS /
      // local-only row (empty driveFolderId) has no folder to search,
      // so we don't imply a missing consent for it.
      const patient = driveFolderId ? patientByFolder.get(driveFolderId) : undefined;
      if (patient && patient.consents.length > 0) {
        lines.push(`    Consent forms:`);
        for (const name of patient.consents) {
          lines.push(`      - ${name}`);
        }
      } else if (driveFolderId) {
        lines.push(`    Consent forms: (none found in patient folder)`);
      }
      lines.push(``);
    }
  }

  if (other.length > 0) {
    lines.push(`Unclassified (${other.length} clip${other.length === 1 ? "" : "s"}):`);
    for (const c of other) {
      lines.push(`  - Unclassified/${formatClipFile(c)}`);
    }
    lines.push(``);
  }

  if (writeSidecars) {
    lines.push(
      `All sidecar files (.description.txt / .txt / .srt) were generated`,
      `from the database at download time. They do NOT exist as separate`,
      `files in Drive — Footage Search bundle is how you obtain them.`,
    );
  } else {
    lines.push(
      `Sidecars (.description.txt / .txt / .srt) were excluded from this`,
      `bundle at download time. Re-download with "Include sidecars"`,
      `checked to obtain the descriptions / transcripts / SRTs.`,
    );
  }

  return lines.join("\n") + "\n";
}

/**
 * Main entry point. Called from the FootageSearch selection tray.
 *  - Pops a folder picker
 *  - Fetches enriched metadata for the selected clip IDs
 *  - Writes the full bundle structure
 *  - Records the download server-side (bumps usage_count)
 *  - Toasts a summary
 */
/**
 * Concurrent streaming downloads per bundle. Picked empirically: most
 * browsers cap concurrent HTTP/1.1 connections to a single origin at 6,
 * and the app server's Drive proxy is the bottleneck per stream. Four
 * gives a clear 3-4× wall-clock win on mixed bundles without making any
 * single stream noticeably slower or saturating the proxy.
 */
const DOWNLOAD_CONCURRENCY = 4;

export async function downloadBundle(args: {
  selectedIds: number[];
  /**
   * Additional footage_files IDs to include in the bundle as audio rows.
   * Resolved by the caller from the per-card "Include accompanying audio"
   * toggles (see selectionCart's audioIncluded()). The audio rows come
   * back from the SAME fetchMetadata() call as selectedIds — they're
   * concatenated before the round-trip — so there's no second request.
   *
   * Routed into a sibling `Audio - MM.DD.YY/` folder rather than the
   * main `Footage - MM.DD.YY/` bundle. Empty / undefined leaves the
   * audio bundle uncreated entirely.
   */
  accompanyingAudioIds?: number[];
  fetchMetadata: (ids: number[]) => Promise<BundleMetadata>;
  recordDownload: (ids: number[]) => Promise<void>;
  /** How videos are delivered — defaults to "download" (stream the
   *  bytes). "shortcut" writes .url files in place of the video;
   *  "sidecars-only" skips both video and shortcut, delivering only
   *  the DB-sourced sidecars + consent PDFs. */
  videoMode?: VideoMode;
  /**
   * Whether to write the DB-sourced sidecar files (.description.txt /
   * .txt / .srt) into the bundle. Defaults to true — the historical
   * behavior. Set false from the normal download flow when the user
   * unchecks "Include sidecars"; videos, consent PDFs, and the manifest
   * are always written regardless. Ignored in "sidecars-only" mode
   * (a sidecars-only bundle with no sidecars would be empty), so the
   * patient/provider sidecar actions are unaffected.
   */
  includeSidecars?: boolean;
  /** Override for the top-level bundle folder prefix. Default is
   *  "Footage" (yielding "Footage - 06.04.26/"). The patient-
   *  sidecars action passes e.g. "Sidecars - Zoe Siver" so the
   *  bundle's purpose reads at a glance. */
  bundleNamePrefix?: string;
  /** Optional callback invoked as bytes stream in. Throttled to ~10/sec. */
  onProgress?: (p: BundleProgress) => void;
}): Promise<BundleResult | null> {
  const {
    selectedIds,
    accompanyingAudioIds = [],
    fetchMetadata,
    recordDownload,
    videoMode = "download",
    includeSidecars = true,
    bundleNamePrefix = "Footage",
    onProgress,
  } = args;

  // Sidecars-only bundles must always write sidecars (that's the whole
  // payload); every other mode honors the includeSidecars toggle.
  const writeSidecars = videoMode === "sidecars-only" ? true : includeSidecars;

  if (selectedIds.length === 0) {
    toast.error("No clips selected.");
    return null;
  }

  const fsa = typeof window !== "undefined" && "showDirectoryPicker" in window;
  if (!fsa) {
    toast.error("Bundle download needs the File System Access API — use Chrome or Edge.");
    return null;
  }

  let root: FileSystemDirectoryHandle;
  try {
    root = await (window as Window & {
      showDirectoryPicker: (o?: { mode?: string }) => Promise<FileSystemDirectoryHandle>;
    }).showDirectoryPicker({ mode: "readwrite" });
  } catch {
    // User cancelled the picker — not an error worth toasting.
    return null;
  }

  const startedAt = Date.now();
  const toastId = toast.loading("Preparing bundle…");

  let meta: BundleMetadata;
  try {
    // Single round-trip — fetch the user's selection AND the
    // accompanying-audio IDs together. Dedup just in case the caller
    // already included an audio ID in selectedIds (e.g. if audio rows
    // ever land in regular selection alongside their videos).
    const allIds = Array.from(new Set([...selectedIds, ...accompanyingAudioIds]));
    meta = await fetchMetadata(allIds);
  } catch (err: any) {
    toast.error(`Couldn't fetch bundle metadata: ${err?.message ?? "unknown"}`, {
      id: toastId,
    });
    return null;
  }

  if (meta.clips.length === 0) {
    toast.error("No accessible clips in the selection (admin/assignment check failed?).", {
      id: toastId,
    });
    return null;
  }

  const downloadDate = new Date();
  const bundleName = `${bundleNamePrefix} - ${formatMMDDYY(downloadDate)}`;
  let bundleFolder: FileSystemDirectoryHandle;
  try {
    bundleFolder = await root.getDirectoryHandle(bundleName, { create: true });
  } catch (err: any) {
    toast.error(`Couldn't create bundle folder: ${err?.message ?? "unknown"}`, {
      id: toastId,
    });
    return null;
  }

  const result: BundleResult = {
    videoSucceeded: 0,
    videoSkipped: 0,
    videoFailed: 0,
    videoConverted: 0,
    sidecarsWritten: 0,
    consentSucceeded: 0,
    consentFailed: 0,
    errors: [],
    failedNames: [],
    failures: [],
    bundleName,
    audioBundleName: null,
    audioSucceeded: 0,
    audioFailed: 0,
    videoMode,
  };

  // Lazy holder for the sibling `Audio - MM.DD.YY/` folder. Created on
  // first audio-row routing, never created in sidecars-only mode (in
  // that mode audio sidecars share the main bundle's per-patient
  // sidecars folder, per the plan). Returning null from getAudioBundle
  // tells the caller to fall back to the main bundle root.
  let audioBundleFolder: FileSystemDirectoryHandle | null = null;
  const audioBundleName = `Audio - ${formatMMDDYY(downloadDate)}`;
  const ensureAudioBundleFolder = async (): Promise<FileSystemDirectoryHandle | null> => {
    if (videoMode === "sidecars-only") return null;
    if (audioBundleFolder) return audioBundleFolder;
    try {
      audioBundleFolder = await root.getDirectoryHandle(audioBundleName, { create: true });
      result.audioBundleName = audioBundleName;
      return audioBundleFolder;
    } catch (err: any) {
      result.errors.push(`audio bundle folder: ${err?.message ?? err}`);
      return null;
    }
  };

  /**
   * Build a Windows-style InternetShortcut file. Double-clicks on
   * Windows open the URL in the default browser. macOS / Linux read
   * it as plain text, so the user can still copy the URL out. We
   * pick `.url` over `.webloc` (macOS-only XML) because the INI
   * format is universally human-readable.
   */
  const buildUrlShortcut = (driveFileId: string): string =>
    `[InternetShortcut]\n` +
    `URL=https://drive.google.com/file/d/${driveFileId}/view\n`;

  /**
   * Build the pointer text for an oversized reference clip. The full master
   * is too large to bundle and was never uploaded — only its audio (for the
   * transcript/search) and a still frame were indexed. This tiny .txt tells
   * the editor exactly where the real file lives on the NAS.
   */
  const buildReferencePointer = (clip: BundleClip): string =>
    [
      `OVERSIZED REFERENCE CLIP — full file NOT included in this bundle.`,
      ``,
      `Clip:  ${clip.renamedName}`,
      `NAS path:`,
      `    ${clip.nasPath ?? "(NAS path unavailable)"}`,
      ``,
      `This master is too large to upload or bundle. Its audio was`,
      `transcribed and a still frame indexed so it stays searchable, but`,
      `the video itself must be pulled directly from the NAS at the path`,
      `above. Any transcript / AI description is in the sidecars/ folder.`,
      ``,
    ].join("\n");

  /**
   * Clip IDs that ended up on disk as a .url shortcut rather than the
   * actual video bytes. Populated in two places:
   *   - Phase 1, when videoMode === "shortcut" (all videos upfront).
   *   - Phase 2, when the user clicks the per-file skip button mid-
   *     stream and the worker converts the aborted download into a
   *     shortcut.
   * Passed to buildManifest so the per-clip rendering matches what
   * the user actually received, regardless of the bundle-wide mode.
   */
  const shortcutClipIds = new Set<number>();

  // Folder handle cache. Key shape:
  //   "subject:A-roll"                            → bundle/A-roll/
  //   "subject:Unclassified"                      → bundle/Unclassified/
  //   "broll:<patientFolderId>"                   → bundle/B-roll/<patientName>/
  //   "sidecars-aroll" / "sidecars-uncl"          → A-roll/sidecars/, Unclassified/sidecars/
  //   "sidecars-broll:<patientFolderId>"          → B-roll/<patientName>/sidecars/
  // One create per folder regardless of how many clips land in it.
  const folderCache = new Map<string, FileSystemDirectoryHandle>();
  const getOrCreate = async (
    parent: FileSystemDirectoryHandle,
    name: string,
    cacheKey: string,
  ): Promise<FileSystemDirectoryHandle> => {
    const cached = folderCache.get(cacheKey);
    if (cached) return cached;
    const handle = await parent.getDirectoryHandle(name, { create: true });
    folderCache.set(cacheKey, handle);
    return handle;
  };

  // Build patient-folder-id → safe patient name lookup once, used by both
  // the per-clip routing and the consent-write loop.
  const patientNameByFolder = new Map<string, string>();
  for (const c of meta.consents) {
    patientNameByFolder.set(
      c.patientFolderId,
      safeFolderName(c.patientName, `Patient ${c.patientFolderId.slice(0, 8)}`),
    );
  }

  /**
   * Resolve the destination for a single clip:
   *   - parent:      where the .mp4 / .mp3 lands
   *   - sidecarsDir: a THUNK that creates + returns the clip's
   *     `sidecars/` subfolder on first call (cached thereafter). Kept
   *     lazy so an empty `sidecars/` folder is never created — it only
   *     materializes when the caller actually has a .description.txt /
   *     .txt / .srt to write. When "Include sidecars" is off (or a clip
   *     has no sidecar content) the thunk is simply never invoked and
   *     no folder appears in the bundle.
   *
   * Audio rows in non-sidecars-only mode route into the sibling
   * `Audio - MM.DD.YY/` bundle (lazily created via ensureAudioBundleFolder).
   * In sidecars-only mode, audio rows share the main bundle's per-
   * subject sidecars folder — the bundle is conceptually one payload.
   *
   * Returns null when we can't set things up (folder create failed, etc.) —
   * the caller logs and moves on.
   */
  const resolveClipDestination = async (
    clip: BundleClip,
  ): Promise<{
    parent: FileSystemDirectoryHandle;
    sidecarsDir: () => Promise<FileSystemDirectoryHandle>;
  } | null> => {
    // Pick the bundle root — audio gets the sibling Audio bundle in any
    // mode that's actually delivering bytes/shortcuts. Sidecars-only mode
    // keeps everything in the main bundle so the editor gets one folder
    // to file away.
    const isAudio = clip.mediaKind === "audio";
    let rootFolder: FileSystemDirectoryHandle = bundleFolder;
    let routePrefix = "main";
    if (isAudio && videoMode !== "sidecars-only") {
      const audioRoot = await ensureAudioBundleFolder();
      if (!audioRoot) {
        result.errors.push(`audio routing for ${clip.renamedName}: no audio bundle folder`);
        return null;
      }
      rootFolder = audioRoot;
      routePrefix = "audio";
    }
    try {
      if (clip.subjectKind === "provider") {
        const aRoll = await getOrCreate(rootFolder, "A-roll", `${routePrefix}:subject:A-roll`);
        return {
          parent: aRoll,
          sidecarsDir: () => getOrCreate(aRoll, "sidecars", `${routePrefix}:sidecars-aroll`),
        };
      }
      if (clip.subjectKind === "patient") {
        const bRoll = await getOrCreate(rootFolder, "B-roll", `${routePrefix}:subject:B-roll`);
        // Name + grouping come from the shared resolver: Drive folder
        // name when there is one, else the row's own subjectName (NAS /
        // local-only rows), else a stable placeholder. Keying an empty-
        // folder row by its name keeps two different name-only patients
        // in separate subfolders instead of colliding under one.
        const { key, name } = resolvePatientGroup(clip, patientNameByFolder);
        const cacheKey = `${routePrefix}:broll:${key}`;
        const patientFolder = await getOrCreate(bRoll, name, cacheKey);
        return {
          parent: patientFolder,
          sidecarsDir: () => getOrCreate(patientFolder, "sidecars", `${cacheKey}:sidecars`),
        };
      }
      // Unclassified (legacy / null subject_kind) — flat layout, no
      // per-patient subdivision since we don't know who owns the clip.
      const uncl = await getOrCreate(rootFolder, "Unclassified", `${routePrefix}:subject:Unclassified`);
      return {
        parent: uncl,
        sidecarsDir: () => getOrCreate(uncl, "sidecars", `${routePrefix}:sidecars-uncl`),
      };
    } catch (err: any) {
      result.errors.push(`folder setup for ${clip.renamedName}: ${err?.message ?? err}`);
      return null;
    }
  };

  // ── Phase 1: Resolve folders, write sidecars + .url shortcuts ────────
  // Everything in this phase is tiny + fast: mkdir calls, plus DB-
  // sourced text writes for sidecars, plus (in shortcut mode) the
  // .url file. We stay sequential here because the operations are
  // cheap and the folder cache benefits from one-at-a-time access.
  // The output is `streamTasks` — the actual bytes-heavy network
  // work, which Phase 2 runs in parallel.
  type StreamTask =
    | {
        kind: "video";
        /**
         * "video" or "audio" — audio clips share the same byte-stream
         * pipeline but their success/failure counts roll up to the
         * audio* fields on the result so the success toast can say
         * "N videos, M audio" accurately.
         */
        mediaKind: "video" | "audio";
        clipId: number;
        name: string;
        /** Needed for the mid-stream skip path so the worker can write a .url
         *  shortcut after aborting the fetch. Null for NAS-only rows (no Drive
         *  viewer URL) — the skip-to-shortcut path is unavailable for those. */
        driveFileId: string | null;
        parent: FileSystemDirectoryHandle;
      }
    | {
        kind: "consent";
        pdfId: string;
        name: string;
        patientFolderId: string;
        folder: FileSystemDirectoryHandle;
      };

  const streamTasks: StreamTask[] = [];

  for (const clip of meta.clips) {
    const clipMediaKind: "video" | "audio" = clip.mediaKind === "audio" ? "audio" : "video";
    const dest = await resolveClipDestination(clip);
    if (!dest) {
      if (clipMediaKind === "audio") result.audioFailed += 1;
      else result.videoFailed += 1;
      result.failedNames.push(clip.renamedName);
      continue;
    }

    // Sidecars — DB-sourced, no Drive round-trip. Written inline.
    // Sanitize the base so illegal chars in the clip name (quotes,
    // slashes) don't make the sidecar writes throw. The known-safe
    // suffixes (.description.txt / .txt / .srt / .url) are appended
    // after sanitizing. `base` is also reused for the .url shortcut
    // below, so it stays declared regardless of writeSidecars.
    const base = sanitizeNamePart(baseName(clip.renamedName)) || "clip";
    // Only touch the sidecars folder when this clip actually has
    // sidecar content to write — resolving dest.sidecarsDir() is what
    // creates the folder, so gating on content keeps an empty
    // `sidecars/` out of the bundle (e.g. when the toggle is off, or a
    // clip has no description/transcript/SRT).
    const hasSidecarContent =
      Boolean(clip.aiDescription) ||
      Boolean(clip.transcriptText) ||
      Boolean(clip.transcriptSrt);
    if (writeSidecars && hasSidecarContent) {
      let sidecarsDir: FileSystemDirectoryHandle | null = null;
      try {
        sidecarsDir = await dest.sidecarsDir();
      } catch (err: any) {
        result.errors.push(`sidecars folder for ${base}: ${err?.message ?? err}`);
      }
      if (sidecarsDir) {
        if (clip.aiDescription) {
          try {
            await writeTextFile(sidecarsDir, `${base}.description.txt`, clip.aiDescription);
            result.sidecarsWritten += 1;
          } catch (err: any) {
            result.errors.push(`${base}.description.txt: ${err?.message ?? err}`);
          }
        }
        if (clip.transcriptText) {
          try {
            await writeTextFile(sidecarsDir, `${base}.txt`, clip.transcriptText);
            result.sidecarsWritten += 1;
          } catch (err: any) {
            result.errors.push(`${base}.txt: ${err?.message ?? err}`);
          }
        }
        if (clip.transcriptSrt) {
          try {
            await writeTextFile(sidecarsDir, `${base}.srt`, clip.transcriptSrt);
            result.sidecarsWritten += 1;
          } catch (err: any) {
            result.errors.push(`${base}.srt: ${err?.message ?? err}`);
          }
        }
      }
    }

    // Media routing:
    //   no Drive copy AND no NAS copy → skip (sidecars still landed above)
    //   sidecars-only                 → skip entirely (no .mp4 / .mp3, no .url)
    //   shortcut (Drive-backed only)  → write .url inline (tiny, no stream)
    //   download (default)            → queue stream task for the parallel pool
    // A row with a nasPath but no driveFileId still has real bytes — the
    // server's /api/footage/stream falls back to the NAS over Tailscale — so
    // it must NOT be treated as local-only.
    if (!clip.driveFileId && !clip.nasPath) {
      if (clipMediaKind === "video") result.videoSkipped += 1;
      // No audio "skipped" counter — local-only audio just doesn't
      // produce a media file. Sidecars already landed above.
      continue;
    }
    // Oversized reference clip — the full master lives only on the NAS and is
    // never bundled. Write a tiny NAS-path pointer .txt so the editor knows
    // exactly where to grab it, and count it as delivered. Done in EVERY mode
    // (including sidecars-only): the pointer is the only deliverable that
    // locates the real file, so it's always useful.
    if (clip.referenceOnly) {
      try {
        await writeTextFile(dest.parent, `${base}.NAS-PATH.txt`, buildReferencePointer(clip));
        if (clipMediaKind === "audio") result.audioSucceeded += 1;
        else result.videoSucceeded += 1;
      } catch (err: any) {
        if (clipMediaKind === "audio") result.audioFailed += 1;
        else result.videoFailed += 1;
        result.failedNames.push(`${base}.NAS-PATH.txt`);
        result.errors.push(`${base}.NAS-PATH.txt: ${err?.message ?? "pointer write failed"}`);
      }
      continue;
    }
    if (videoMode === "sidecars-only") {
      // Editor already has the media in Drive — this bundle exists
      // purely to deliver the DB-sourced sidecars. Skip the media
      // write entirely (we don't even bump videoSkipped, since the
      // skip isn't a data-state issue, it's the configured mode).
      continue;
    }
    // Shortcut mode only works for Drive-backed rows (a .url points at the
    // Drive viewer). A NAS-only row has no Drive URL, so fall through to a
    // real download instead of emitting a broken shortcut.
    if (videoMode === "shortcut" && clip.driveFileId) {
      try {
        await writeTextFile(
          dest.parent,
          `${base}.url`,
          buildUrlShortcut(clip.driveFileId),
        );
        if (clipMediaKind === "audio") result.audioSucceeded += 1;
        else result.videoSucceeded += 1;
        shortcutClipIds.add(clip.id);
      } catch (err: any) {
        if (clipMediaKind === "audio") result.audioFailed += 1;
        else result.videoFailed += 1;
        result.failedNames.push(`${base}.url`);
        result.errors.push(`${base}.url: ${err?.message ?? "shortcut write failed"}`);
      }
    } else {
      streamTasks.push({
        kind: "video",
        mediaKind: clipMediaKind,
        clipId: clip.id,
        // Sanitized so a slash / quote / colon in the renamed clip name
        // can't make getFileHandle() throw and fail the whole stream.
        name: safeFileName(clip.renamedName),
        driveFileId: clip.driveFileId,
        parent: dest.parent,
      });
    }
  }

  // Consents always stream as full PDF downloads — videoMode only
  // affects the video bytes, not the consent forms (those tend to be
  // small one-pagers and the user always wants the actual file).
  for (const c of meta.consents) {
    if (c.pdfs.length === 0) continue;
    const patientName = patientNameByFolder.get(c.patientFolderId);
    if (!patientName) continue;
    let patientFolder: FileSystemDirectoryHandle;
    try {
      const bRoll = await getOrCreate(bundleFolder, "B-roll", "subject:B-roll");
      patientFolder = await getOrCreate(bRoll, patientName, `broll:${c.patientFolderId}`);
    } catch (err: any) {
      result.errors.push(`consent folder for ${patientName}: ${err?.message ?? err}`);
      continue;
    }
    const writtenForPatient = new Set<string>();
    for (const pdf of c.pdfs) {
      if (writtenForPatient.has(pdf.name)) continue;
      writtenForPatient.add(pdf.name);
      streamTasks.push({
        kind: "consent",
        pdfId: pdf.id,
        // Consent PDF names come straight from Drive and often carry a
        // preferred name in quotes — sanitize before it hits the disk.
        name: safeFileName(pdf.name),
        patientFolderId: c.patientFolderId,
        folder: patientFolder,
      });
    }
  }

  // ── Phase 2: Parallel streaming pool ─────────────────────────────────
  const totalFiles = streamTasks.length;

  // Single persistent loading toast — per-file detail lives in the
  // download overlay's progress bars. Quieter than firing one toast
  // per file (which we used to do).
  toast.loading(
    videoMode === "sidecars-only"
      ? `Writing sidecars (${result.sidecarsWritten} so far${
          totalFiles > 0
            ? ` + ${totalFiles} consent${totalFiles === 1 ? "" : "s"}`
            : ""
        })…`
      : videoMode === "shortcut"
        ? `Building bundle (${totalFiles} consent${totalFiles === 1 ? "" : "s"} + shortcuts)…`
        : `Downloading bundle (${totalFiles} file${totalFiles === 1 ? "" : "s"})…`,
    { id: toastId },
  );

  // Map from task index → its current in-flight state. Stays small —
  // capped at DOWNLOAD_CONCURRENCY entries since a worker only adds
  // an entry before its stream and deletes it after.
  const inFlight = new Map<
    number,
    {
      key: string;
      name: string;
      received: number;
      total: number | null;
      kind: "video" | "consent";
      skip?: () => void;
    }
  >();
  let completed = 0;

  // Throttle progress emission to ~10 events/sec. Boundary events
  // (file-start, file-done) always force-fire. Without throttling,
  // 4 parallel streams on a fast pipe can hit onProgress hundreds
  // of times per second total.
  let lastEmit = 0;
  const THROTTLE_MS = 100;
  const emit = (force = false) => {
    const now = performance.now();
    if (!force && now - lastEmit < THROTTLE_MS) return;
    lastEmit = now;
    onProgress?.({
      totalFiles,
      completed,
      inFlight: Array.from(inFlight.values()),
    });
  };

  // Initial 0-of-N emit so the overlay renders the empty state
  // immediately rather than waiting for the first byte.
  emit(true);

  // Simple worker-pool: a shared counter hands out the next task
  // index to whichever worker asks first. Each worker runs serially
  // until the queue is drained. Errors are caught per-task so one
  // failed stream doesn't stop the rest of the bundle.
  let nextTaskIdx = 0;
  const runOneTask = async (idx: number): Promise<void> => {
    const task = streamTasks[idx];
    const key = `${idx}:${task.name}`;
    // Per-task AbortController. Only used for video tasks today —
    // consent PDFs have no shortcut equivalent, so their entries
    // expose no skip() and the controller stays dormant.
    const controller = new AbortController();
    let userSkipped = false;
    inFlight.set(idx, {
      key,
      name: task.name,
      received: 0,
      total: null,
      kind: task.kind,
      skip:
        task.kind === "video"
          ? () => {
              if (userSkipped) return;
              userSkipped = true;
              controller.abort();
            }
          : undefined,
    });
    emit(true);

    const url =
      task.kind === "video"
        ? // ?download=1 → server sends Content-Disposition: attachment
          // so save-as semantics work. Inline preview (the <video> in
          // Footage Search) hits the same endpoint without the flag.
          `/api/footage/stream?fileId=${task.clipId}&download=1`
        : `/api/footage/consent-pdf?driveFileId=${encodeURIComponent(task.pdfId)}` +
          `&patientFolderId=${encodeURIComponent(task.patientFolderId)}`;
    const dir = task.kind === "video" ? task.parent : task.folder;

    try {
      await streamUrlToFile(
        dir,
        task.name,
        url,
        (received, total) => {
          const entry = inFlight.get(idx);
          if (entry) {
            entry.received = received;
            entry.total = total;
          }
          emit();
        },
        controller.signal,
      );
      if (task.kind === "video") {
        if (task.mediaKind === "audio") result.audioSucceeded += 1;
        else result.videoSucceeded += 1;
      } else {
        result.consentSucceeded += 1;
      }
    } catch (err: any) {
      // User-initiated skip on a media stream → convert to a .url
      // shortcut in the same destination folder. Counts as a success
      // because the deliverable (Drive pointer) actually landed; the
      // manifest reflects shortcut vs media accurately via shortcutClipIds.
      // NAS-only rows have no Drive viewer URL, so there's nothing to write —
      // a skip there just counts as a (user-chosen) failure.
      if (userSkipped && task.kind === "video" && task.driveFileId) {
        try {
          await writeTextFile(
            task.parent,
            `${baseName(task.name)}.url`,
            buildUrlShortcut(task.driveFileId),
          );
          if (task.mediaKind === "audio") result.audioSucceeded += 1;
          else result.videoSucceeded += 1;
          result.videoConverted += 1;
          shortcutClipIds.add(task.clipId);
        } catch (shortcutErr: any) {
          if (task.mediaKind === "audio") result.audioFailed += 1;
          else result.videoFailed += 1;
          const failName = `${baseName(task.name)}.url`;
          result.failedNames.push(failName);
          result.errors.push(
            `${failName}: ${shortcutErr?.message ?? "shortcut write failed"}`,
          );
          result.failures.push({
            name: failName,
            kind: task.mediaKind === "audio" ? "audio" : "video",
            clipId: task.clipId,
            driveFileId: task.driveFileId,
            error: shortcutErr?.message ?? "shortcut write failed",
          });
        }
      } else if (task.kind === "video") {
        if (task.mediaKind === "audio") result.audioFailed += 1;
        else result.videoFailed += 1;
        result.failedNames.push(task.name);
        result.errors.push(`${task.name}: ${err?.message ?? "stream failed"}`);
        result.failures.push({
          name: task.name,
          kind: task.mediaKind === "audio" ? "audio" : "video",
          clipId: task.clipId,
          driveFileId: task.driveFileId,
          error: err?.message ?? "stream failed",
        });
      } else {
        result.consentFailed += 1;
        result.failedNames.push(task.name);
        result.errors.push(`${task.name}: ${err?.message ?? "consent fetch failed"}`);
        result.failures.push({
          name: task.name,
          kind: "consent",
          clipId: null,
          driveFileId: task.pdfId,
          error: err?.message ?? "consent fetch failed",
        });
      }
    } finally {
      inFlight.delete(idx);
      completed += 1;
      emit(true);
    }
  };

  const workerCount = Math.min(DOWNLOAD_CONCURRENCY, totalFiles);
  const workers = Array.from({ length: workerCount }, async () => {
    while (true) {
      const idx = nextTaskIdx++;
      if (idx >= totalFiles) return;
      await runOneTask(idx);
    }
  });
  await Promise.all(workers);

  // ── Manifest ─────────────────────────────────────────────────────────
  try {
    await writeTextFile(
      bundleFolder,
      // Leading "!" sorts the manifest to the top of the folder. We can't
      // use "*" — the File System Access API rejects it (and " < > : / \
      // | ?) on every platform, so getFileHandle would throw and the
      // manifest would silently fail to write. "!" is accepted and still
      // sorts ahead of letters/numbers in Finder.
      "!Manifest.txt",
      buildManifest(meta, downloadDate, videoMode, shortcutClipIds, writeSidecars),
    );
  } catch (err: any) {
    result.errors.push(`!Manifest.txt: ${err?.message ?? "write failed"}`);
  }

  // ── Record usage server-side (best-effort) ───────────────────────────
  try {
    const downloadedIds = meta.clips
      .filter((c) => c.driveFileId !== null)
      .map((c) => c.id);
    if (downloadedIds.length > 0) {
      await recordDownload(downloadedIds);
    }
  } catch (err: any) {
    console.warn("[bundleDownload] recordDownload failed:", err?.message ?? err);
  }

  const seconds = Math.round((Date.now() - startedAt) / 1000);
  const audioSuffix =
    result.audioSucceeded > 0
      ? `, ${result.audioSucceeded} audio file(s) in ${result.audioBundleName ?? audioBundleName}`
      : "";
  const headline =
    videoMode === "sidecars-only"
      ? `Sidecars ready in ${seconds}s — ${result.sidecarsWritten} file(s), ${result.consentSucceeded} consent(s)`
      : `Bundle ready in ${seconds}s — ${result.videoSucceeded} ${
          videoMode === "shortcut" ? "shortcut(s)" : "video(s)"
        }, ${result.sidecarsWritten} sidecar(s), ${result.consentSucceeded} consent(s)${audioSuffix}`;
  const failures = result.videoFailed + result.consentFailed + result.audioFailed;
  if (failures > 0) {
    // Name the failed files right in the toast so the user doesn't have
    // to crack open the browser console. Cap the list so a large
    // partial-failure doesn't produce a wall-of-text toast; the full
    // detail (with HTTP status / message per file) still logs below.
    const MAX_NAMED = 4;
    const named = result.failedNames.slice(0, MAX_NAMED).join(", ");
    const overflow = result.failedNames.length - MAX_NAMED;
    const nameList = named
      ? `: ${named}${overflow > 0 ? ` +${overflow} more` : ""}`
      : "";
    toast.warning(
      `${headline}. ${failures} failed${nameList}. Full details in the browser console.`,
      { id: toastId, duration: 12000 },
    );
    for (const e of result.errors) console.warn("[bundleDownload]", e);
  } else if (result.videoSkipped > 0) {
    toast.success(
      `${headline}. ${result.videoSkipped} local-only clip(s) had no video to include.`,
      { id: toastId, duration: 10000 },
    );
  } else {
    toast.success(headline, { id: toastId, duration: 8000 });
  }

  return result;
}
